import React, {useEffect, useState} from 'react';
import { Paper } from '@mui/material';
import SubcatchChartBase from './SubcatchChartBase';
import OutfallChartBase from './OutfallChartBase';
import SubcatchChartResults from './SubcatchChartResults';
import OutfallChartResults from './OutfallChartResults';

import { useMapContext } from '../../../contexts/MapContext';
import LoadingLogo from '../../../utils/LoadingLogo';


const GraphPanel = () => {
    const {
        stormMode,
        selectedFeature,
        mapClickInfo,
        outFileExists,
        selectedStormOption,
        userData,
        devPlanID,
    } = useMapContext();
    const [runoffChange, setRunoffChange] = React.useState(null);
    const [infilChange, setInfilChange] = React.useState(null);
    const [selectedFeatureData, setSelectedFeatureData] = React.useState(selectedFeature.Data);
    const [selectedLayer, setSelectedLayer] = React.useState(mapClickInfo.selectedLayer);
    const [loading, setLoading] = useState(false);

    const [infiltrationSum, setInfiltrationSum] = React.useState(null);
    const [newInfiltrationSum, setNewInfiltrationSum] = React.useState(null);
    const [rainfallData, setRainfallData] = React.useState(null);
    const [timeseriesData, setTimeseriesData] = React.useState(null);
    const [rainfallSum, setRainfallSum] = React.useState(null);
    const [timeseriesSum, setTimeseriesSum] = React.useState(null);
    const [newTimeseriesData, setNewTimeseriesData] = React.useState(null);
    const [newTimeseriesSum, setNewTimeseriesSum] = React.useState(null);


// Makes call to API to get timeseries data for the selected feature
// variables : either rainfall or runoff
  const fetchTimeseriesData = async (assetType, assetName, variable, selectedStormOption, plan = 'og') => {
    const url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/read/${selectedStormOption}/${plan}/${assetType}/${assetName}/${variable}`;
    try {
      const response = await fetch(url);
      const data = await response.json();

      if (data.Error) {
        console.error(`API error: ${data.Error}`);
        return;
      }

      if (variable === 'runoff') {
        if (data.x) {
          if (plan === 'og') {
            return {timeseriesData: data, timeseriesSum: (data.y.reduce((a, b) => a + b, 0)*900*7.48052/1000000).toPrecision(3)}
            // ================================================================== RETURN
          } else {
            console.log('TIMESERIES_DEBUG: Setting newTimeseriesData for runoff', {
              storm: selectedStormOption,
              plan,
              hasData: !!data.x,
              dataPoints: data.x?.length
            });
            return {newTimeseriesData: data, newTimeseriesSum: (data.y.reduce((a, b) => a + b, 0)*900*7.48052/1000000).toPrecision(3)}
            // ================================================================== RETURN
          }
        } else {
          return {timeseriesData: {x: [-1], y: [-1]}, timeseriesSum: -1}
          // ================================================================== RETURN
        }
      } else if (variable === 'infiltration') {
        if (data.x) {
          let perv_area;
          if (selectedFeature.Data) {
            console.log("TEST 1");
            perv_area = selectedFeature.Data.AREA*43560*(100-selectedFeature.Data.IMPERV)/100;
          } else {
            // This is for the preload request -- using 400 as an arbitrary area

            // (ERROR) Kaming : selectedFeature.Data.IMPERV is NULL in this condition. Not able to read properties of NULL.
            //                  Do we make an assumed value for selectedFeature.Data.IMPERV?
            //perv_area = 400*43560*(100-selectedFeature.Data.IMPERV)/100;
            perv_area = 400*43560*(100-50)/100 // Switched just so it doesn't throw an error - Jacob
          }


          if (plan === 'og') {
            return {infiltrationData: data, infiltrationSum: (data.y.reduce((a, b) => a + b, 0)*perv_area*0.25*(1/12)*7.48052/1000000).toPrecision(3)}
            // ================================================================== RETURN
          } else {
            return {newInfiltrationData: data, newInfiltrationSum: (data.y.reduce((a, b) => a + b, 0)*perv_area*0.25*(1/12)*7.48052/1000000).toPrecision(3)}
            // ================================================================== RETURN
          }
        } else {
          return {infiltrationData: {x: [-1], y: [-1]}, infiltrationSum: -1}
          // ================================================================== RETURN
        }

      } else if (variable === 'total_inflow') {
        if (data.x) {
          if (plan === 'og') {
            return {timeseriesData: data, timeseriesSum: (data.y.reduce((a, b) => a + b, 0)*900*7.48052/1000000).toPrecision(4)}
            // ================================================================== RETURN
          } else {
            console.log('TIMESERIES_DEBUG: Setting newTimeseriesData for total_inflow', {
              storm: selectedStormOption,
              plan,
              hasData: !!data.x,
              dataPoints: data.x?.length
            });
            return {newTimeseriesData: data, newTimeseriesSum: (data.y.reduce((a, b) => a + b, 0)*900*7.48052/1000000).toPrecision(4)}
            // ================================================================== RETURN
          }
        } else {
          return {timeseriesData: {x: [-1], y: [-1]}, timeseriesSum: -1}
          // ================================================================== RETURN
        }

      }
    } catch (err) {
      console.error('TIMESERIES_DEBUG: Error fetching data:', {
        url,
        error: err.message
      });
      return null;
    }
  };

  // Makes a call to the API to get rainfall data for a given subcatchment
    const fetchRainfallData = async (assetName, selectedStormOption) => {
      const url = `https://2471d2z813.execute-api.us-east-2.amazonaws.com/read/${selectedStormOption}/og/subcatchment/${assetName}/rainfall`;
      console.log('Endpoint (fetchRainfallData):', url);
      const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
      try {
        const response = await fetch(url);
        const data = await response.json();

        if (data.Error) {
          console.error(`API error: ${data.Error}`);
          console.log('Retrying after 1 second...');
          await delay(1000); // Wait 1 second
          return fetchRainfallData(assetName, selectedStormOption);
        }
        if (data.x) {
          const scaledData = {
            x: data.x,
            y: data.y.map(value => value * 0.25) // rainfall is given in in/hr, measurements are once every 15 min
          }
          return {rainfallData: scaledData, rainfallSum: (scaledData.y.reduce((a, b) => a + b, 0)).toPrecision(2)};
        } else {
          return {rainfallData: {x: [-1], y: [0]}, rainfallSum: -1};
        }
      } catch (err) {
        console.error('Error loading sample data:', err);
        return {rainfallData: {x: [-1], y: [0]}, rainfallSum: -1};
      }
    };

    // Fetch all necessary data upon selecting a new feature.
    useEffect(() => {
      setLoading(true);
      // bump request sequence to invalidate previous requests
      const requestId = (GraphPanel.requestSeq = (GraphPanel.requestSeq || 0) + 1);
      const isCurrent = () => GraphPanel.requestSeq === requestId;

      if (!selectedFeature?.Data) {
        setSelectedFeatureData(null);
        setTimeseriesData(null);
        setRainfallData(null);
        setTimeseriesSum(null);
        setRainfallSum(null);
        setInfiltrationSum(null);
        setNewTimeseriesData(null);
        setNewTimeseriesSum(null);
        setNewInfiltrationSum(null);
        setRunoffChange(null);
        setInfilChange(null);
        setLoading(false);
        return;
      }

      const refreshData = async () => {
        setRunoffChange(null);
        setInfilChange(null);
        setTimeseriesSum(null);
        setInfiltrationSum(null);
        setTimeseriesData(null);
        setNewTimeseriesData(null);

        let newData = {
          timeseriesData: null,
          timeseriesSum: null,
          rainfallData: null,
          rainfallSum: null,
          infiltrationData: null,
          infiltrationSum: null,
          newTimeseriesData: null,
          newTimeseriesSum: null,
          newInfiltrationData: null,
          newInfiltrationSum: null,
        };

        if (!isCurrent()) return;
        setSelectedFeatureData(selectedFeature.Data);
        setSelectedLayer(mapClickInfo.selectedLayer);
        const clickedFeature = selectedFeature.Data;

        // =============== Clicked on a subcatchment =====================
        if ('AREA' in clickedFeature) {
          if (selectedFeature.ID && mapClickInfo.assetName) {
            let data;
            data = await fetchRainfallData(clickedFeature.NAME, selectedStormOption);
            if (!isCurrent()) return; // abort if stale
            if (data) {
              for (let key in data) {
                if (data[key] !== null) newData[key] = data[key];
              }
            }
            setRainfallData(newData.rainfallData);
            setLoading(false);

            data = await fetchTimeseriesData('subcatchment', clickedFeature.NAME, 'runoff', selectedStormOption);
            if (!isCurrent()) return;
            if (data) {
              for (let key in data) {
                if (data[key] !== null) newData[key] = data[key];
              }
            }
            data = await fetchTimeseriesData('subcatchment', clickedFeature.NAME, 'infiltration', selectedStormOption);
            if (!isCurrent()) return;
            if (data) {
              for (let key in data) {
                if (data[key] !== null) newData[key] = data[key];
              }
            }
            if (outFileExists && userData && devPlanID) {
              let plan_name = `${userData.id}_${devPlanID}`;
              data = await fetchTimeseriesData('subcatchment', clickedFeature.NAME, 'runoff', selectedStormOption, plan_name);
              if (!isCurrent()) return;
              if (data) {
                for (let key in data) {
                  if (data[key] !== null) newData[key] = data[key];
                }
              }
              data = await fetchTimeseriesData('subcatchment', clickedFeature.NAME, 'infiltration', selectedStormOption, plan_name);
              if (!isCurrent()) return;
              if (data) {
                for (let key in data) {
                  if (data[key] !== null) newData[key] = data[key];
                }
              }
            }
          }
        } else {
          // =============== Clicked on an outfall =====================
          let data = await fetchTimeseriesData('node', clickedFeature.NAME, 'total_inflow', selectedStormOption);
          if (!isCurrent()) return;
          if (data) {
            for (let key in data) {
              if (data[key] !== null) newData[key] = data[key];
            }
          }
          if (outFileExists && userData && devPlanID) {
            let plan_name = `${userData.id}_${devPlanID}`;
            data = await fetchTimeseriesData('node', clickedFeature.NAME, 'total_inflow', selectedStormOption, plan_name);
            if (!isCurrent()) return;
            if (data) {
              for (let key in data) {
                if (data[key] !== null) newData[key] = data[key];
              }
            }
          }
        }

        if (!isCurrent()) return;
        setTimeseriesData(newData.timeseriesData);
        setRainfallSum(newData.rainfallSum);
        setTimeseriesSum(newData.timeseriesSum);
        setNewTimeseriesData(newData.newTimeseriesData);
        setNewTimeseriesSum(newData.newTimeseriesSum);
        setInfiltrationSum(newData.infiltrationSum);
        setNewInfiltrationSum(newData.newInfiltrationSum);

        if (!outFileExists || newData.timeseriesSum == null || newData.newTimeseriesSum == null) {
          setRunoffChange(null);
          setInfilChange(null);
          setLoading(false);
          return;
        }
        setRunoffChange((newData.newTimeseriesSum - newData.timeseriesSum).toFixed(3));
        if (newData.newInfiltrationSum != null && newData.infiltrationSum != null) {
          setInfilChange((newData.newInfiltrationSum - newData.infiltrationSum).toFixed(3));
        } else {
          setInfilChange(null);
        }

        setLoading(false);
      };

      refreshData();
    }, [selectedFeature.ID, devPlanID]);

    if (!stormMode || !selectedFeatureData) return null;

    return (
        <div
        style={{
            width: '100%',
            height: '300px',
            backgroundColor: '#071317',
            opacity: 0.9,
            borderRadius: '5px',
            display: 'flex',
            flexDirection: 'column',
        }}
        >
          {false && (
            <h4
              style={{
                color: '#94cfec',
                textAlign: 'left',
                marginLeft: '20px',
                marginBottom: '10px',
              }}
            >
              Subcatchment {selectedLayer === 'subcatchment' ? 'Runoff (cfs)' : 'Overflow (cfs)'}
            </h4>
          )}


            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0 20px',
                }}
            >
                {selectedLayer === 'subcatchment' && (
                    <Paper
                        elevation={3}
                        style={{
                            backgroundColor: '#071317',
                            width: '100px',
                            height: '60px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginBottom: '10px',
                            marginLeft: '10px',
                        }}
                    >
                        <div style={{ textAlign: 'center', color: '#94cfec', fontSize: '0.9em' }}>
                            💧 Precip: <br />
                            <b>{rainfallSum}</b>"
                        </div>
                    </Paper>
                )}

                {selectedLayer === 'subcatchment' && (
                    <Paper
                        elevation={3}
                        style={{
                            backgroundColor: '#071317',
                            width: '100px',
                            height: '60px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginBottom: '10px',
                            marginLeft: '10px',
                        }}
                    >
                        <div style={{ textAlign: 'center', color: '#94cfec', fontSize: '0.9em' }}>
                            🌊 Runoff: <br />
                            <b>{timeseriesSum ? timeseriesSum : '---'}</b> MG<br/>
                            {timeseriesSum != null && runoffChange !== null &&  !isNaN(runoffChange) && runoffChange !== '0.000' &&(
                                <span style={{ color: runoffChange > 0 ? '#f76060' : '#78f178' }}>
                                    {runoffChange > 0 ? '+' : ''}{runoffChange} MG
                                </span>
                            )}
                        </div>
                    </Paper>
                )}

                {selectedLayer === 'subcatchment' && (
                    <Paper
                        elevation={3}
                        style={{
                            backgroundColor: '#071317',
                            width: '100px',
                            height: '60px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginBottom: '10px',
                            marginLeft: '10px',
                        }}
                    >
                        <div style={{ textAlign: 'center', color: '#94cfec', fontSize: '0.9em' }}>
                            🌱 Infiltration: <br />
                            <b>{infiltrationSum ? infiltrationSum : '---'}</b> MG <br/>
                            {infiltrationSum !== null && infilChange !== null && !isNaN(infilChange) && infilChange !== '0.000' && (
                              <span style={{ color: infilChange < 0 ? '#f76060' : '#78f178' }}>
                                {infilChange > 0 ? '+' : ''}{infilChange} MG
                              </span>
                            )}
                        </div>
                    </Paper>
                )}

                {selectedLayer !== 'subcatchment' && (
                    <Paper
                        elevation={3}
                        style={{
                            backgroundColor: '#071317',
                            width: '100px',
                            height: '60px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginBottom: '10px',
                            marginLeft: '10px',
                        }}
                    >
                        <div style={{ textAlign: 'center', color: '#94cfec', fontSize: '0.9em' }}>
                            🌊 Overflow: <br />
                            <b>{timeseriesSum}</b> MG <br/>
                          {runoffChange !== null &&  !isNaN(runoffChange) &&(
                            <span style={{ color: runoffChange > 0 ? '#f76060' : '#78f178' }}>
                                    {runoffChange > 0 ? '+' : ''}{runoffChange} MG
                                </span>
                          )}
                        </div>
                    </Paper>
                )}
            </div>

            <div
                style={{
                    flex: 1,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '0 20px',
                }}
            >
                {loading ? (<LoadingLogo />) : (timeseriesData && timeseriesData.x ? (
                        selectedLayer === 'subcatchment' ? (
                            outFileExists && newTimeseriesData && rainfallData ? (
                                <SubcatchChartResults x={timeseriesData.x} y1={timeseriesData.y} y2={rainfallData.y} y3={newTimeseriesData.y} />
                            ) : ( rainfallData ? (
                                <SubcatchChartBase x={timeseriesData.x} y1={timeseriesData.y} y2={rainfallData.y} />
                                ) : (
                                    <LoadingLogo />
                                )
                            )
                        ) : (
                            outFileExists && newTimeseriesData ? (
                                // Is an outfall & we have result data
                                <OutfallChartResults x={timeseriesData.x} y={timeseriesData.y} y2={newTimeseriesData.y} selectedLayer={selectedLayer} />
                            ) : (
                                // Is an outfall & we don't have result data
                                <OutfallChartBase x={timeseriesData.x} y={timeseriesData.y} selectedLayer={selectedLayer} />
                            )
                        )
                    ) : (<LoadingLogo />)
                )}
            </div>
        </div>
    );
};

export default GraphPanel;
